/**
 * ConstructionWorkerService
 * 
 * Service class for managing construction worker operations.
 * Provides CRUD operations, authentication, search, and password management
 * for construction workers (現場 workers) in the payslip system.
 * 
 * Features:
 * - Construction worker authentication (login/logout)
 * - CRUD operations (create, read, update, delete)
 * - Construction worker search functionality
 * - Password change functionality
 * - Employee code existence checking
 * - Auto-generation of employee codes (C##### format)
 * - JWT token management
 * 
 * Authentication:
 * - Uses JWT tokens stored in sessionStorage
 * - Automatically includes token in all authenticated requests
 * - Login stores token, logout clears token
 * 
 * Employee Code Format:
 * - Format: C##### (C followed by 5 digits, e.g., C12345)
 * - Auto-generated when creating new construction workers
 * 
 * Differences from EmployeeService:
 * - Employee codes are auto-generated in C##### format
 * - No user type field (all are construction workers)
 * - Separate API endpoints for construction workers
 * 
 * Used by:
 * - UserManagementMain component for construction worker management
 * - Login component for construction worker authentication
 * - Various components for construction worker data fetching
 * 
 * @class
 */

import { CONSTRUCTION_WORKER_API_URL } from '../config/apiConfig';

/**
 * Construction Worker Interface
 * 
 * Represents a construction worker in the system.
 * 
 * @interface ConstructionWorker
 * @property {number} [id] - Optional database ID
 * @property {string} empCode - Employee code (C##### format)
 * @property {string} name - Construction worker full name
 * @property {string} [password] - Optional password (used for creation/updates)
 */
export interface ConstructionWorker {
  id?: number;
  empCode: string;
  name: string;
  password?: string;
}

/**
 * Generic API Response Interface
 * 
 * Standardized response format for all API operations.
 * Includes nextEmpCode for auto-generation endpoints.
 * 
 * @interface ApiResponse
 * @template T - Type of data returned in the response
 * @property {boolean} success - Whether the operation was successful
 * @property {string} [message] - Optional message describing the result
 * @property {T} [data] - Optional response data
 * @property {boolean} [exists] - Optional flag indicating if resource exists
 * @property {string} [nextEmpCode] - Optional next employee code for auto-generation
 */
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  exists?: boolean;
  nextEmpCode?: string;
}

/**
 * Login Response Interface
 * 
 * Response format for authentication operations.
 * 
 * @interface LoginResponse
 * @property {boolean} success - Whether login was successful
 * @property {string} [message] - Optional message describing the result
 * @property {string} [token] - JWT token if login successful
 * @property {Object} [employee] - Employee information if login successful
 * @property {string} employee.empCode - Employee code
 * @property {string} employee.name - Employee name
 * @property {string} employee.userType - User type (always 'construction worker')
 */
export interface LoginResponse {
  success: boolean;
  message?: string;
  token?: string;
  employee?: {
    empCode: string;
    name: string;
    userType: string;
  };
}

/**
 * ConstructionWorkerService Class
 * 
 * Main service class for construction worker operations.
 * Manages all API interactions for construction worker management.
 * 
 * @class
 */
class ConstructionWorkerService {
  /**
   * Base URL for construction worker API endpoints.
   * 
   * @private
   */
  private baseUrl = CONSTRUCTION_WORKER_API_URL;

  /**
   * Gets JWT token from sessionStorage.
   * 
   * Retrieves the authentication token stored in sessionStorage.
   * Used for authenticated API requests.
   * 
   * @private
   * @returns {string | null} JWT token or null if not found
   * 
   * @example
   * ```typescript
   * const token = this.getAuthToken();
   * // Returns token string or null
   * ```
   */
  private getAuthToken(): string | null {
    return sessionStorage.getItem('authToken');
  }

  /**
   * Gets common HTTP headers with authorization.
   * 
   * Constructs headers object with Content-Type and Authorization
   * (if token exists) for authenticated API requests.
   * 
   * @private
   * @returns {HeadersInit} Headers object with Content-Type and optional Authorization
   * 
   * @example
   * ```typescript
   * const headers = this.getHeaders();
   * // Returns { 'Content-Type': 'application/json', 'Authorization': 'Bearer ...' }
   * ```
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Gets the next available employee code.
   * 
   * Fetches the next auto-generated employee code in C##### format.
   * Used when creating new construction workers.
   * 
   * @returns {Promise<ApiResponse<{ nextEmpCode: string }>>} Response with next employee code
   * @async
   * 
   * @example
   * ```typescript
   * const result = await constructionWorkerService.getNextEmpCode();
   * if (result.success && result.nextEmpCode) {
   *   const newCode = result.nextEmpCode; // e.g., "C12345"
   * }
   * ```
   */
  async getNextEmpCode(): Promise<ApiResponse<{ nextEmpCode: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/next-emp-code`, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '次の従業員コードの取得に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Creates a new construction worker.
   * 
   * Creates a construction worker with the provided data.
   * Employee code must be provided in C##### format.
   * 
   * @param constructionWorker - Construction worker data to create
   * @returns {Promise<ApiResponse<ConstructionWorker>>} Response with created construction worker data
   * @async
   * 
   * @example
   * ```typescript
   * const newWorker = {
   *   empCode: 'C12345',
   *   name: '田中花子',
   *   password: 'Password123'
   * };
   * const result = await constructionWorkerService.createConstructionWorker(newWorker);
   * ```
   */
  async createConstructionWorker(constructionWorker: ConstructionWorker): Promise<ApiResponse<ConstructionWorker>> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(constructionWorker),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '現場作業員の登録に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Creates a new construction worker with auto-generated employee code.
   * 
   * Creates a construction worker and automatically generates the employee code
   * in C##### format. This is the recommended method for creating new workers.
   * 
   * @param name - Construction worker name
   * @param password - Construction worker password
   * @returns {Promise<ApiResponse<ConstructionWorker>>} Response with created construction worker data
   * @async
   * 
   * @example
   * ```typescript
   * const result = await constructionWorkerService.createConstructionWorkerWithAutoCode(
   *   '田中花子',
   *   'Password123'
   * );
   * // Employee code is automatically generated
   * ```
   */
  async createConstructionWorkerWithAutoCode(name: string, password: string): Promise<ApiResponse<ConstructionWorker>> {
    try {
      const response = await fetch(`${this.baseUrl}/create-with-auto-code`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ name, password }),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '現場作業員の登録に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Gets all construction workers.
   * 
   * Fetches all construction workers from the backend.
   * 
   * @returns {Promise<ApiResponse<ConstructionWorker[]>>} Response with array of all construction workers
   * @async
   * 
   * @example
   * ```typescript
   * const result = await constructionWorkerService.getAllConstructionWorkers();
   * if (result.success && result.data) {
   *   const workers = result.data;
   * }
   * ```
   */
  async getAllConstructionWorkers(): Promise<ApiResponse<ConstructionWorker[]>> {
    try {
      const response = await fetch(this.baseUrl, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '現場作業員一覧の取得に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Searches for construction workers by query.
   * 
   * Searches construction workers by employee code or name.
   * If query is empty, returns all construction workers.
   * 
   * @param query - Search query (employee code or name)
   * @returns {Promise<ApiResponse<ConstructionWorker[]>>} Response with matching construction workers
   * @async
   * 
   * @example
   * ```typescript
   * // Search for construction workers with code or name containing "C123"
   * const result = await constructionWorkerService.searchConstructionWorkers('C123');
   * ```
   */
  async searchConstructionWorkers(query: string): Promise<ApiResponse<ConstructionWorker[]>> {
    try {
      const url = query ? `${this.baseUrl}/search?query=${encodeURIComponent(query)}` : this.baseUrl;
      const response = await fetch(url, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '現場作業員の検索に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Gets a construction worker by employee code.
   * 
   * Fetches a specific construction worker by their employee code.
   * 
   * @param empCode - Employee code (C##### format)
   * @returns {Promise<ApiResponse<ConstructionWorker>>} Response with construction worker data
   * @async
   * 
   * @example
   * ```typescript
   * const result = await constructionWorkerService.getConstructionWorkerByEmpCode('C12345');
   * ```
   */
  async getConstructionWorkerByEmpCode(empCode: string): Promise<ApiResponse<ConstructionWorker>> {
    try {
      const response = await fetch(`${this.baseUrl}/${empCode}`, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '現場作業員情報の取得に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Updates an existing construction worker.
   * 
   * Updates construction worker information by employee code.
   * Password is optional and only updated if provided.
   * Employee code cannot be changed.
   * 
   * @param empCode - Employee code to update
   * @param constructionWorker - Updated construction worker data
   * @returns {Promise<ApiResponse<ConstructionWorker>>} Response with updated construction worker data
   * @async
   * 
   * @example
   * ```typescript
   * const updated = {
   *   empCode: 'C12345',
   *   name: '田中花子',
   *   password: 'NewPassword123' // Optional
   * };
   * const result = await constructionWorkerService.updateConstructionWorker('C12345', updated);
   * ```
   */
  async updateConstructionWorker(empCode: string, constructionWorker: ConstructionWorker): Promise<ApiResponse<ConstructionWorker>> {
    try {
      const response = await fetch(`${this.baseUrl}/${empCode}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(constructionWorker),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '現場作業員情報の更新に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Deletes a construction worker.
   * 
   * Deletes a construction worker by employee code.
   * WARNING: This operation is irreversible.
   * 
   * @param empCode - Employee code to delete
   * @returns {Promise<ApiResponse<void>>} Response indicating success or failure
   * @async
   * 
   * @example
   * ```typescript
   * const result = await constructionWorkerService.deleteConstructionWorker('C12345');
   * ```
   */
  async deleteConstructionWorker(empCode: string, deleteLinkedOffice = false): Promise<ApiResponse<void>> {
    try {
      const q = deleteLinkedOffice ? '?deleteLinkedOffice=true' : '';
      const response = await fetch(`${this.baseUrl}/${encodeURIComponent(empCode)}${q}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '現場作業員の削除に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Whether 本社 and/or 現場 row exists for this employee code (dual-account delete UI).
   */
  async getAccountLinkStatus(empCode: string): Promise<{
    success: boolean;
    office?: boolean;
    construction?: boolean;
    message?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/link-status/${encodeURIComponent(empCode)}`, {
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: 'アカウント連携状態の取得に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Checks if an employee code already exists.
   * 
   * Used for validation before creating new construction workers.
   * 
   * @param empCode - Employee code to check (C##### format)
   * @returns {Promise<ApiResponse<{ exists: boolean }>>} Response with exists flag
   * @async
   * 
   * @example
   * ```typescript
   * const result = await constructionWorkerService.checkEmployeeCodeExists('C12345');
   * if (result.data?.exists) {
   *   // Employee code already exists
   * }
   * ```
   */
  async checkEmployeeCodeExists(empCode: string): Promise<ApiResponse<{ exists: boolean }>> {
    try {
      const response = await fetch(`${this.baseUrl}/check-code/${empCode}`, {
        headers: this.getHeaders(),
      });
      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: '従業員コードの確認に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Changes a construction worker's password.
   * 
   * Updates construction worker password after verifying current password.
   * 
   * @param empCode - Employee code
   * @param currentPassword - Current password for verification
   * @param newPassword - New password to set
   * @returns {Promise<ApiResponse<void>>} Response indicating success or failure
   * @async
   * 
   * @example
   * ```typescript
   * const result = await constructionWorkerService.changePassword(
   *   'C12345',
   *   'OldPassword123',
   *   'NewPassword123'
   * );
   * ```
   */
  async changePassword(empCode: string, currentPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}/change-password`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ empCode, currentPassword, newPassword }),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return {
        success: false,
        message: 'パスワードの変更に失敗しました（通信エラー）',
      };
    }
  }

  /**
   * Authenticates a construction worker and logs them in.
   * 
   * Sends login credentials to the server and stores the JWT token
   * in sessionStorage if authentication is successful.
   * 
   * @param empCode - Employee code (C##### format)
   * @param password - Construction worker password
   * @returns {Promise<LoginResponse>} Login response with token and worker info
   * @async
   * 
   * @example
   * ```typescript
   * const result = await constructionWorkerService.login('C12345', 'Password123');
   * if (result.success) {
   *   // User is logged in, token stored in sessionStorage
   * }
   * ```
   */
  async login(empCode: string, password: string): Promise<LoginResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ empCode, password }),
      });

      const result = await response.json();
      
      // Store token if login is successful
      if (result.success && result.token) {
        sessionStorage.setItem('authToken', result.token);
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        message: 'ログインに失敗しました',
      };
    }
  }

  /**
   * Logs out the current construction worker.
   * 
   * Clears authentication token and current employee data from sessionStorage.
   * Should be called when user logs out or session expires.
   * 
   * @function
   * 
   * @example
   * ```typescript
   * constructionWorkerService.logout();
   * // Token and employee data cleared from sessionStorage
   * ```
   */
  logout(): void {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('currentEmployee');
  }
}

/**
 * Export singleton instance of ConstructionWorkerService.
 * 
 * Use this instance throughout the application to interact
 * with the construction worker API.
 * 
 * @example
 * ```typescript
 * import ConstructionWorkerService from './services/ConstructionWorkerService';
 * 
 * // Use the service
 * const workers = await ConstructionWorkerService.getAllConstructionWorkers();
 * ```
 */
export default new ConstructionWorkerService();
